import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import UpworkApprovalSettings from './UpworkApprovalSettings';
import UpworkPreviewFields from './UpworkPreviewFields';
import { upworkAction } from '../services/upworkActionService';

export default function UpworkApprovalQueue() {
  const { canApproveUpworkActions, canUpdateTenantConfig } = usePermissions();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const permitted = canApproveUpworkActions();
  const reload = async () => { setData(await upworkAction()); };
  useEffect(() => {
    if (!permitted) return;
    const abort = new AbortController();
    upworkAction('', 'GET', null, abort.signal).then(setData).catch((e) => { if (!abort.signal.aborted) setError(e.message); });
    return () => abort.abort();
  }, [permitted]);
  const act = async (row, cancel = false) => {
    setBusy(row._id); setError(''); setNotice('');
    try {
      let result;
      if (cancel) result = await upworkAction(`/${row._id}/changes`, 'POST');
      else {
        if (row.status === 'pending') await upworkAction(`/${row._id}/approve`, 'POST', { previewHash: row.previewHash });
        result = await upworkAction(`/${row._id}/execute`, 'POST', { previewHash: row.previewHash });
      }
      setNotice(result.status === 'submitted' ? 'Upwork confirmed the submission.' : result.status === 'outcome_unknown'
        ? 'The outcome is uncertain. Check Upwork before taking any further action. Budget remains reserved.'
        : result.status === 'cancelled' ? 'Changes requested. The requester can revise the draft.' : 'Nothing was confirmed. Review the request status.');
      await reload();
    } catch (e) { setError(e.message); await reload().catch(() => {}); }
    finally { setBusy(''); }
  };
  if (!permitted) return <p className="p-6" role="alert">You need Upwork approval permission to review these requests.</p>;
  return <section className="mx-auto max-w-4xl p-6 space-y-4">
    <h1 className="text-xl font-semibold">Upwork approvals</h1>
    <p className="text-sm text-gray-600">Review the exact proposal and cost. Approve and send performs the final submission.</p>
    {error && <p role="alert" className="text-red-700">{error}</p>}
    {notice && <p role="status">{notice}</p>}
    {!data ? <p role="status">Loading approvals…</p> : <>
      {!data.writesEnabled && <p role="status">Upwork submissions are currently disabled.</p>}
      <p className="text-sm">Workspace limits: {data.policy.perProposal} Connects per proposal, {data.policy.perDay} per UTC day, {data.policy.perWeek} per UTC week. Boosting is off.</p>
      {canUpdateTenantConfig() && <UpworkApprovalSettings policy={data.policy} onSaved={reload} />}
      {!data.requests.length && <p>No proposals are waiting for approval.</p>}
      {data.requests.map((row) => {
        const expired = new Date(row.expiresAt) <= new Date();
        const ready = ['pending', 'approved'].includes(row.status) && !expired && data.writesEnabled;
        return <article key={row._id} className="border rounded-md p-4 space-y-3">
          <div className="flex flex-wrap justify-between gap-2"><Link to={`/proposals/${row.proposalId}`} className="text-primary-700 underline">Open proposal</Link><span>{row.status.replaceAll('_', ' ')}</span></div>
          {row.job && <p><a className="text-primary-700 underline font-medium" href={`https://www.upwork.com/jobs/~02${row.job.id}`} target="_blank" rel="noopener noreferrer">{row.job.title}</a><span className="block text-xs text-gray-600">Upwork job {row.job.id}</span></p>}
          <p>Submitting as {row.accountName || 'your selected Upwork account'}{row.snapshot.team_org_id ? ' with the selected agency team' : ''}</p>
          <p className="font-medium">{row.connectsCost ?? 'Unknown'} Connects · Bid ${row.snapshot.charged_amount} {row.job?.pricingType === 'hourly' ? '/ hour' : 'fixed'}</p>
          <div className="whitespace-pre-wrap text-sm">{row.snapshot.cover_letter}</div>
          {row.snapshot.answers?.map((a, i) => <div key={i} className="text-sm"><p className="font-medium">{a.question}</p><p className="whitespace-pre-wrap">{a.answer}</p></div>)}
          {row.preview && <div className="rounded-md bg-gray-50 p-3 text-sm"><h3 className="font-semibold mb-2">Upwork preview</h3><UpworkPreviewFields value={row.preview.preview} /></div>}
          {expired && ready === false && ['pending', 'approved'].includes(row.status) && <p role="status">This preview expired. Cancel it and prepare a new one.</p>}
          {row.status === 'outcome_unknown' && <p role="alert">Do not resubmit. Verify the result on Upwork; Connects remain reserved until reconciliation.</p>}
          <div className="flex gap-2">
            {ready && <button className="rounded-md bg-primary-600 text-white py-1.5 px-3 text-sm disabled:opacity-50" disabled={Boolean(busy)} onClick={() => act(row)}>{busy === row._id ? 'Sending…' : 'Approve and send'}</button>}
            {['preparing', 'pending', 'approved'].includes(row.status) && <button className="rounded-md border py-1.5 px-3 text-sm" disabled={Boolean(busy)} onClick={() => act(row, true)}>Request changes</button>}
          </div>
        </article>;
      })}
    </>}
    <button className="rounded-md border py-1.5 px-3 text-sm" disabled={Boolean(busy)} onClick={() => { setError(''); reload().catch((e) => setError(e.message)); }}>Refresh</button>
  </section>;
}
